<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import MessageAPI from '../../../../api/inbox/message';
import FunnelAPI from '../../../../api/funnel';
import KanbanAPI from '../../../../api/kanban';
import { emitter } from 'shared/helpers/mitt';
import Button from '../../../../components-next/button/Button.vue';

const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccount?.id);
const selectedFunnel = computed(
  () => store.getters['funnel/getSelectedFunnel']
);

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['close', 'send']);
const { t } = useI18n();

const loading = ref(false);
const sendingMessage = ref(false);
const templates = ref([]);
const selectedTemplate = ref(null);
const selectedConditions = ref({
  status: '',
  priority: '',
  hasAttachments: false,
  stage: '',
  assignee: '',
  lastActivity: '',
  tags: [],
  customFields: [],
});
const kanbanItems = ref([]);
const selectedConversations = ref(new Set());
const isProcessing = ref(false);
const processedItems = ref([]);
const successCount = ref(0);
const errorCount = ref(0);
const progress = ref(0);
const showFilters = ref(false);
const hasSelections = computed(
  () => selectedConversations.value.size > 0 && filteredItems.value.length > 0
);
const canSendMessages = computed(
  () => !!selectedTemplate.value && hasSelections.value
);

// Buscar templates disponíveis
const fetchTemplates = async () => {
  try {
    loading.value = true;
    const response = await FunnelAPI.get();
    const allTemplates = [];

    response.data.forEach(funnel => {
      Object.values(funnel.stages).forEach(stage => {
        if (stage.message_templates) {
          allTemplates.push(...stage.message_templates);
        }
      });
    });

    templates.value = allTemplates;
  } catch (error) {
    console.error('Erro ao carregar templates:', error);
  } finally {
    loading.value = false;
  }
};

// Buscar itens do Kanban
const fetchKanbanItems = async () => {
  try {
    if (!selectedFunnel.value?.id) return;

    // Se props.items está vazio, buscar todos os itens do funil
    if (props.items.length === 0) {
      const response = await KanbanAPI.getItems(selectedFunnel.value.id);
      console.log('[DEBUG] Response completa:', response);
      console.log('[DEBUG] Response data:', response.data);

      // A resposta tem estrutura {items: Array, pagination: {...}}
      const itemsData = response.data?.items || [];
      console.log('[DEBUG] Items data (array):', itemsData.length);

      kanbanItems.value = itemsData;
      console.log(
        '[DEBUG] Usando todos os itens do funil:',
        kanbanItems.value.length
      );
    } else {
      // Usar diretamente os itens passados como props
      kanbanItems.value = props.items;
      console.log('[DEBUG] Usando itens dos props:', kanbanItems.value.length);
    }

    console.log(
      '[DEBUG] Items com conversa:',
      kanbanItems.value.filter(item => item.item_details?.conversation_id)
        .length
    );
    console.log(
      '[DEBUG] IDs das conversas:',
      kanbanItems.value.map(item => ({
        itemId: item.id,
        conversationId: item.item_details?.conversation_id,
        hasConversation: !!item.item_details?.conversation,
      }))
    );
  } catch (error) {
    console.error('Erro ao carregar itens:', error);
  }
};

// Computed para itens filtrados com todas as condições
const filteredItems = computed(() => {
  return kanbanItems.value.filter(item => {
    // Verifica se tem conversation_id
    if (!item.item_details?.conversation_id) return false;

    // Filtro por status
    if (
      selectedConditions.value.status &&
      item.item_details.status !== selectedConditions.value.status
    ) {
      return false;
    }

    // Filtro por prioridade
    if (
      selectedConditions.value.priority &&
      item.item_details.priority !== selectedConditions.value.priority
    ) {
      return false;
    }

    // Filtro por anexos
    if (selectedConditions.value.hasAttachments && !item.attachments?.length) {
      return false;
    }

    // Filtro por etapa
    if (
      selectedConditions.value.stage &&
      item.funnel_stage !== selectedConditions.value.stage
    ) {
      return false;
    }

    // Filtro por responsável
    if (
      selectedConditions.value.assignee &&
      item.item_details.agent_id !== selectedConditions.value.assignee
    ) {
      return false;
    }

    // Filtro por última atividade
    if (selectedConditions.value.lastActivity) {
      const lastActivity = new Date(
        item.item_details.conversation?.last_activity_at
      );
      const now = new Date();
      const diffDays = Math.floor((now - lastActivity) / (1000 * 60 * 60 * 24));

      switch (selectedConditions.value.lastActivity) {
        case 'today':
          if (diffDays > 0) return false;
          break;
        case 'week':
          if (diffDays > 7) return false;
          break;
        case 'month':
          if (diffDays > 30) return false;
          break;
      }
    }

    return true;
  });
});

const handleToggleConversation = item => {
  const id = item.item_details.conversation_id;
  if (selectedConversations.value.has(id)) {
    selectedConversations.value.delete(id);
  } else {
    selectedConversations.value.add(id);
  }
};

const handleSelectAll = () => {
  if (selectedConversations.value.size === filteredItems.value.length) {
    selectedConversations.value.clear();
  } else {
    selectedConversations.value = new Set(
      filteredItems.value.map(item => item.item_details.conversation_id)
    );
  }
};

const handleSend = async () => {
  if (!selectedTemplate.value || selectedConversations.value.size === 0) return;

  try {
    isProcessing.value = true;
    sendingMessage.value = true;
    processedItems.value = [];
    successCount.value = 0;
    errorCount.value = 0;
    progress.value = 0;

    const itemsToProcess = filteredItems.value.filter(item =>
      selectedConversations.value.has(item.item_details.conversation_id)
    );

    for (const item of itemsToProcess) {
      try {
        const conversationId = item.item_details.conversation_id;
        const inboxId = item.item_details.conversation?.inbox_id || 1;

        await MessageAPI.create({
          conversationId,
          message: selectedTemplate.value.content,
          private: false,
          message_type: 'outgoing',
          inbox_id: inboxId,
        });

        processedItems.value.push({
          id: item.id,
          title: item.item_details.title,
          conversationId,
          status: 'success',
        });
        successCount.value++;
      } catch (error) {
        processedItems.value.push({
          id: item.id,
          title: item.item_details.title,
          conversationId: item.item_details.conversation_id,
          status: 'error',
          error: error.message,
        });
        errorCount.value++;
      }

      progress.value =
        (processedItems.value.length / itemsToProcess.length) * 100;
    }

    emitter.emit('newToastMessage', {
      message: `Processo concluído: ${successCount.value} mensagens enviadas, ${errorCount.value} falhas`,
      action: { type: successCount.value > 0 ? 'success' : 'error' },
    });
  } catch (error) {
    emitter.emit('newToastMessage', {
      message: 'Erro ao processar envios',
      action: { type: 'error' },
    });
  } finally {
    sendingMessage.value = false;
  }
};

const handleClose = () => {
  if (isProcessing.value) {
    isProcessing.value = false;
    processedItems.value = [];
    successCount.value = 0;
    errorCount.value = 0;
    progress.value = 0;
  }
  emit('close');
};

onMounted(() => {
  fetchTemplates();
  fetchKanbanItems();
});
</script>

<template>
  <div class="space-y-6">
    <!-- Tela de Progresso -->
    <div v-if="isProcessing" class="space-y-6">
      <div class="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
        <div class="text-center space-y-2">
          <p class="text-xs font-semibold uppercase tracking-wide text-woot-500">
            {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_BADGE') }}
          </p>
          <h3 class="text-xl font-semibold">
            {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_TITLE') }}
          </h3>
          <p class="text-sm text-slate-500">
            {{
              t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_DESCRIPTION', {
                count: processedItems.length,
                total: selectedConversations.size,
              })
            }}
          </p>
        </div>

        <!-- Barra de Progresso -->
        <div class="relative mt-6 space-y-3">
          <div class="flex items-center justify-between text-xs font-semibold">
            <span
              class="inline-flex items-center rounded-full px-3 py-1"
              :class="{
                'bg-emerald-50 text-emerald-600': progress === 100,
                'bg-woot-50 text-woot-600': progress < 100,
              }"
            >
              {{ Math.round(progress) }}%
            </span>
            <span class="text-slate-500">
              {{ successCount }}
              {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.SUCCESS') }} •
              {{ errorCount }} {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.ERROR') }}
            </span>
          </div>
          <div class="h-3 overflow-hidden rounded-full bg-slate-100">
            <div
              :style="{ width: `${progress}%` }"
              class="h-full rounded-full transition-all duration-500"
              :class="{
                'bg-emerald-500': progress === 100,
                'bg-woot-500': progress < 100,
              }"
            />
          </div>
        </div>
      </div>

      <!-- Lista de Resultados -->
      <div class="rounded-2xl border border-slate-100 bg-white shadow-sm">
        <div
          class="flex items-center justify-between border-b px-6 py-4 text-sm"
        >
          <div class="font-medium">
          {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_TITLE') }}
          </div>
          <span class="text-xs text-slate-500">
            {{
              t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.PROCESSING_RESULTS', {
                count: processedItems.length,
              })
            }}
          </span>
        </div>
        <div class="max-h-[430px] divide-y overflow-y-auto">
          <div
            v-for="item in processedItems"
            :key="item.id"
            class="flex items-center gap-3 px-6 py-4 text-sm hover:bg-slate-50"
          >
            <span
              class="flex h-10 w-10 items-center justify-center rounded-full"
              :class="
                item.status === 'success'
                  ? 'bg-emerald-50 text-emerald-500'
                  : 'bg-ruby-50 text-ruby-500'
              "
            >
              <fluent-icon
                :icon="
                  item.status === 'success'
                    ? 'checkmark-circle'
                    : 'error-circle'
                "
                size="18"
              />
            </span>
            <div class="flex-1">
              <p class="font-medium">{{ item.title }}</p>
              <p class="text-xs text-slate-500">
                {{
                  t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.CONVERSATION_ID', {
                    id: item.conversationId,
                  })
                }}
              </p>
              <p v-if="item.error" class="mt-2 text-xs text-ruby-500">
                {{ item.error }}
              </p>
            </div>
            <span
              class="rounded-full px-3 py-1 text-xs font-semibold"
              :class="
                item.status === 'success'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-ruby-100 text-ruby-700'
              "
            >
              {{
                item.status === 'success'
                  ? t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.STATUS_SUCCESS')
                  : t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.STATUS_ERROR')
              }}
            </span>
          </div>
        </div>
      </div>

      <!-- Ações -->
      <div class="flex justify-end">
        <Button variant="ghost" color="slate" size="sm" @click="handleClose">
          {{ t('KANBAN.BULK_ACTIONS.CANCEL') }}
        </Button>
      </div>
    </div>

    <!-- Conteúdo Original -->
    <div v-else class="space-y-6">
      <div
        class="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm space-y-2"
      >
        <p class="text-xs font-semibold uppercase tracking-wide text-woot-500">
          {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.HEADER') }}
        </p>
        <div class="flex items-center justify-between">
          <div>
            <h3 class="text-xl font-semibold">
              {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.TITLE') }}
            </h3>
            <p class="text-sm text-slate-500">
              {{
                t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.SUBTITLE', {
                  count: selectedConversations.size,
                })
              }}
            </p>
          </div>
          <div class="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-2">
            <p class="text-xs uppercase tracking-wide text-slate-500">
              {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.SELECTED_CONVERSATIONS_LABEL') }}
            </p>
            <p class="text-lg font-semibold text-slate-900">
              {{ selectedConversations.size }}/{{ filteredItems.length }}
            </p>
          </div>
        </div>
      </div>

      <!-- Lista de conversas e Switch de Filtros -->
      <div class="space-y-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p class="text-xs uppercase tracking-wide text-slate-500">
              {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.CONVERSATION_SECTION_LABEL') }}
            </p>
            <h4 class="text-lg font-semibold">
              {{
                t('KANBAN.BULK_ACTIONS.SELECTED_CONVERSATIONS', {
                  count: selectedConversations.size,
                  total: filteredItems.length,
                })
              }}
            </h4>
          </div>
          <div class="flex flex-wrap items-center gap-3">
            <button
              class="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium transition hover:border-slate-300"
              @click="showFilters = !showFilters"
            >
              <fluent-icon icon="filter" size="16" />
              {{
                showFilters
                  ? t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.HIDE_FILTERS')
                  : t('KANBAN.BULK_ACTIONS.FILTER_ITEMS')
              }}
            </button>
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              @click="handleSelectAll"
            >
              {{
                selectedConversations.size === filteredItems.length
                  ? t('KANBAN.BULK_ACTIONS.DESELECT_ALL')
                  : t('KANBAN.BULK_ACTIONS.SELECT_ALL')
              }}
            </Button>
          </div>
        </div>

        <!-- Bloco de Filtros -->
        <div
          v-if="showFilters"
          class="space-y-4 rounded-2xl border border-slate-100 bg-woot-50/70 p-5"
        >
          <!-- Resumo dos Filtros -->
          <div class="space-y-3">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <h4 class="text-sm font-semibold">
                {{ t('KANBAN.BULK_ACTIONS.FILTERS_ACTIVE') }}
              </h4>
              <Button
                variant="ghost"
                color="slate"
                size="sm"
                @click="
                  selectedConditions = {
                    status: '',
                    priority: '',
                    hasAttachments: false,
                    stage: '',
                    assignee: '',
                    lastActivity: '',
                    tags: [],
                    customFields: [],
                  }
                "
              >
                {{ t('KANBAN.BULK_ACTIONS.CLEAR_FILTERS') }}
              </Button>
            </div>
            <div class="flex flex-wrap gap-2 text-xs text-slate-600">
              <template v-for="(value, key) in selectedConditions" :key="key">
                <span
                  v-if="value && typeof value !== 'boolean'"
                  class="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 shadow-sm"
                >
                  {{ key }}: {{ value }}
                  <button
                    class="text-slate-400 hover:text-slate-600"
                    @click="selectedConditions[key] = ''"
                  >
                    ×
                  </button>
                </span>
                <span
                  v-else-if="value === true"
                  class="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 shadow-sm"
                >
                  {{ key }}
                  <button
                    class="text-slate-400 hover:text-slate-600"
                    @click="selectedConditions[key] = false"
                  >
                    ×
                  </button>
                </span>
              </template>
            </div>
          </div>

          <!-- Condições de Filtro -->
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <h4 class="text-sm font-semibold">
                {{ t('KANBAN.BULK_ACTIONS.FILTER_CONVERSATIONS') }}
              </h4>
              <span class="text-xs text-slate-500">
                {{
                  t('KANBAN.BULK_ACTIONS.FILTER_CONVERSATIONS_COUNT', {
                    count: filteredItems.length,
                  })
                }}
              </span>
            </div>

            <div class="grid gap-4 md:grid-cols-2">
              <div>
                <label class="mb-2 block text-xs font-semibold text-slate-600">
                  {{ t('KANBAN.BULK_ACTIONS.ITEM_STATUS.TITLE') }}
                </label>
                <select
                  v-model="selectedConditions.status"
                  class="w-full rounded-xl border-slate-200 text-sm"
                >
                  <option value="">
                    {{ t('KANBAN.BULK_ACTIONS.ITEM_STATUS.ALL') }}
                  </option>
                  <option value="open">
                    {{ t('KANBAN.BULK_ACTIONS.ITEM_STATUS.OPEN') }}
                  </option>
                  <option value="won">
                    {{ t('KANBAN.BULK_ACTIONS.ITEM_STATUS.WON') }}
                  </option>
                  <option value="lost">
                    {{ t('KANBAN.BULK_ACTIONS.ITEM_STATUS.LOST') }}
                  </option>
                </select>
              </div>

              <div>
                <label class="mb-2 block text-xs font-semibold text-slate-600">
                  {{ t('KANBAN.BULK_ACTIONS.PRIORITY.TITLE') }}
                </label>
                <select
                  v-model="selectedConditions.priority"
                  class="w-full rounded-xl border-slate-200 text-sm"
                >
                  <option value="">
                    {{ t('KANBAN.BULK_ACTIONS.PRIORITY.ALL') }}
                  </option>
                  <option value="urgent">
                    {{ t('KANBAN.BULK_ACTIONS.PRIORITY.URGENT') }}
                  </option>
                  <option value="high">
                    {{ t('KANBAN.BULK_ACTIONS.PRIORITY.HIGH') }}
                  </option>
                  <option value="medium">
                    {{ t('KANBAN.BULK_ACTIONS.PRIORITY.MEDIUM') }}
                  </option>
                  <option value="low">
                    {{ t('KANBAN.BULK_ACTIONS.PRIORITY.LOW') }}
                  </option>
                </select>
              </div>

              <div>
                <label class="mb-2 block text-xs font-semibold text-slate-600">
                  {{ t('KANBAN.BULK_ACTIONS.STAGE.TITLE') }}
                </label>
                <select
                  v-model="selectedConditions.stage"
                  class="w-full rounded-xl border-slate-200 text-sm"
                >
                  <option value="">
                    {{ t('KANBAN.BULK_ACTIONS.STAGE.ALL') }}
                  </option>
                  <option
                    v-for="stage in Object.keys(
                      selectedFunnel?.stages || {}
                    )"
                    :key="stage"
                    :value="stage"
                  >
                    {{ selectedFunnel?.stages[stage]?.name }}
                  </option>
                </select>
              </div>

              <div>
                <label class="mb-2 block text-xs font-semibold text-slate-600">
                  {{ t('KANBAN.BULK_ACTIONS.LAST_ACTIVITY.TITLE') }}
                </label>
                <select
                  v-model="selectedConditions.lastActivity"
                  class="w-full rounded-xl border-slate-200 text-sm"
                >
                  <option value="">
                    {{ t('KANBAN.BULK_ACTIONS.LAST_ACTIVITY.ANY') }}
                  </option>
                  <option value="today">
                    {{ t('KANBAN.BULK_ACTIONS.LAST_ACTIVITY.TODAY') }}
                  </option>
                  <option value="week">
                    {{ t('KANBAN.BULK_ACTIONS.LAST_ACTIVITY.WEEK') }}
                  </option>
                  <option value="month">
                    {{ t('KANBAN.BULK_ACTIONS.LAST_ACTIVITY.MONTH') }}
                  </option>
                </select>
              </div>
            </div>

            <div class="flex items-center gap-4">
              <label class="flex items-center gap-2">
                <input
                  type="checkbox"
                  v-model="selectedConditions.hasAttachments"
                  class="h-4 w-4 rounded border-slate-300 text-woot-600 focus:ring-woot-500"
                />
                <span class="text-sm font-medium text-slate-600">
                  {{ t('KANBAN.BULK_ACTIONS.HAS_ATTACHMENTS') }}
                </span>
              </label>
            </div>
          </div>
        </div>

        <!-- Lista de Conversas -->
        <div v-if="filteredItems.length > 0" class="max-h-64 overflow-y-auto">
          <div
            v-for="item in filteredItems"
            :key="item.id"
            class="flex cursor-pointer items-start gap-4 rounded-2xl border border-slate-100 px-4 py-3 text-sm shadow-sm transition hover:border-woot-200 hover:bg-woot-25"
            @click="handleToggleConversation(item)"
          >
            <input
              type="checkbox"
              :checked="
                selectedConversations.has(item.item_details.conversation_id)
              "
              class="mt-1 h-4 w-4 rounded border-slate-300 text-woot-600 focus:ring-woot-500"
              @click.stop
            />
            <div class="flex-1">
              <p class="font-medium text-slate-900">
                {{ item.item_details.title }}
              </p>
              <p class="text-xs text-slate-500">
                {{
                  t('KANBAN.BULK_ACTIONS.CONVERSATION_ID', {
                    id: item.item_details.conversation_id,
                  })
                }}
              </p>
            </div>
          </div>
        </div>
        <div v-else class="rounded-xl border border-dashed border-slate-200 bg-slate-50 py-6 text-center text-sm text-slate-500">
          {{ t('KANBAN.BULK_ACTIONS.NO_CONVERSATIONS_AVAILABLE') }}
        </div>
      </div>

      <!-- Seleção de Template -->
      <div class="space-y-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-xs uppercase tracking-wide text-slate-500">
              {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.MESSAGE_SECTION_LABEL') }}
            </p>
            <h4 class="text-lg font-semibold">
              {{ t('KANBAN.BULK_ACTIONS.SELECT_MESSAGE_TEMPLATE') }}
            </h4>
          </div>
          <span
            class="inline-flex items-center rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold"
            :class="selectedTemplate ? 'text-emerald-600' : 'text-slate-400'"
          >
            {{
              selectedTemplate
                ? t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.TEMPLATE_SELECTED')
                : t('KANBAN.BULK_ACTIONS.SEND_MESSAGE.TEMPLATE_PENDING')
            }}
          </span>
        </div>

        <div v-if="loading" class="flex justify-center py-6">
          <span class="loading-spinner" />
        </div>

        <div v-else class="space-y-4">
          <select
            v-model="selectedTemplate"
            class="w-full rounded-2xl border border-slate-200 p-3 text-sm"
            :class="{ 'border-ruby-500': !selectedTemplate }"
          >
            <option value="">
              {{ t('KANBAN.BULK_ACTIONS.MESSAGE_TEMPLATE_SELECT') }}
            </option>
            <option
              v-for="template in templates"
              :key="template.id"
              :value="template"
            >
              {{ template.title }}
            </option>
          </select>

          <div
            v-if="selectedTemplate"
            class="rounded-2xl border border-slate-100 bg-slate-50 p-4 text-sm text-slate-600"
          >
            {{ selectedTemplate.content }}
          </div>
        </div>
      </div>

      <!-- Resumo -->
      <div class="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
        <p class="text-sm text-slate-600">
          {{
            t('KANBAN.BULK_ACTIONS.MESSAGE_SUMMARY', {
              count: selectedConversations.size,
              total: items.length,
            })
          }}
        </p>
      </div>

      <!-- Ações -->
      <div class="flex justify-end gap-2">
        <Button variant="ghost" color="slate" size="sm" @click="handleClose">
          {{ t('KANBAN.BULK_ACTIONS.CANCEL') }}
        </Button>
        <Button
          variant="solid"
          color="blue"
          size="sm"
          :isLoading="sendingMessage"
          :disabled="!canSendMessages"
          @click="handleSend"
        >
          {{ t('KANBAN.BULK_ACTIONS.SEND_MESSAGES') }}
        </Button>
      </div>
    </div>
  </div>
</template>
